import moment from 'moment';
import { history } from 'umi';
import { getBasePath } from '@/utils/utils';
import { getCompanyDetailsPath } from '@intelligence/utils/topchartsUtils';
import {
  round,
  getFormatNumPlus,
  getRoundIntegerNum,
  toFirstUpperCase,
  queryJumpable,
} from '@gp/utils/common';
import DataTypeUtils from '@gp/utils/DataTypeUtils';
import { OHERSKEY, MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import { StudioJumpSystem } from '../enums';
import { STUDIO_JUMP_SYSTEMS } from '../constants';

const getNumValueText = (
  value: number | string | undefined,
): number | string => {
  // // @ts-ignore
  // let numValue = Number.parseFloat(value);
  // if (Number.isNaN(numValue)) return '-';
  // return numValue;
  return getRoundIntegerNum(value);
};

const getValueWithUnitText = (value: number | string | undefined): string => {
  // @ts-ignore
  let numValue = Number.parseFloat(value);
  if (Number.isNaN(numValue)) return '-';
  numValue = round(numValue);
  if (numValue === 0) return '0';
  return '$' + getFormatNumPlus(numValue);
};

const getLastYearAndCurrentYear = (options: {
  lastYear?: string;
  currentYear: string;
}) => {
  const { lastYear: lastYearOptions, currentYear: currentYearOptions } =
    options || {};

  let result = {
    lastYear: '',
    currentYear: '',
  };
  if (!lastYearOptions && !currentYearOptions) {
    const now = moment();
    result.lastYear = moment(now).subtract('year', 1).format('YYYY') || '';
    result.currentYear = now.format('YYYY') || '';
  } else {
    if (lastYearOptions) {
      result.lastYear = lastYearOptions;
      result.currentYear = String(Number.parseInt(result.lastYear, 10) + 1);
    } else {
      result.currentYear = currentYearOptions;
      result.lastYear = String(Number.parseInt(result.currentYear, 10) - 1);
    }
  }

  return result;
};

const getOptionsByStringValues = (data: string[]): ISelectOptionWithGroup[] => {
  const result: ISelectOptionWithGroup[] = [];
  if (!DataTypeUtils.isArray(data)) return result;
  let otherOption = null;
  for (const item of data) {
    if (!item?.trim()) continue;
    let text = item || '';
    if (item === 'OTHER') {
      text = OHERSKEY;
      otherOption = {
        text,
        value: item,
      };
    } else {
      try {
        // @ts-ignore
        text = Object.values(JSON.parse(text))[0] || text;
      } catch (exception) {
        // text = toFirstUpperCase(text);
      }
    }
    if (item !== 'OTHER') {
      result.push({
        text,
        value: item,
      });
    }
  }

  if (otherOption !== null) {
    result.push(otherOption);
  }

  return result;
};

const formatStudioOptions = (data?: { [key: string]: any[] }): any[] => {
  if (!data || Object.prototype.toString.call(data) !== '[object Object]') return [];
  return Object.entries(data)?.map(([key, val]) => {
    const parentVal = key || '';
    const parentText = toFirstUpperCase(key);
    const childrenTmp = val?.map((item) => {
      const childVal = `${parentVal}_split_${item?.id}`;
      const childText = toFirstUpperCase(item?.name);
      return {
        disabled: false,
        value: childVal,
        text: childText,
        textCN: childText,
      };
    }) || [];

    return {
      disabled: false,
      value: parentVal,
      text: parentText,
      textCN: parentText,
      children: childrenTmp,
    };
  });
};

const getStudioDataTypeDefaultValue = (
  data?: {
    [key: string]: any[],
  },
): {
  markets: string[],
  regions: string[],
  type: 'market' | 'region',
} => {
  const res: {
    markets: string[],
    regions: string[],
    type: 'market' | 'region',
  } = {
    markets: [],
    regions: ['base'],
    type: 'region',
  };
  if (!data || Object.prototype.toString.call(data) !== '[object Object]') return res;
  let newMarkets: string[] = [];
  for (const key in data) {
    if (key === 'base' && Array.isArray(data?.[key])) {
      newMarkets = data?.[key]?.map((t) => {
        return `base_split_${t?.id}`;
      });
    }
  }
  res.markets = newMarkets;
  if (!res.markets.length) {
    res.regions = [];
  }

  return res;
}

const formatStudioValues = (data: {
  type: 'region' | 'market';
  regions: string[];
  markets: string[];
}): {
  [key: string]: string[];
} => {
  const { markets } = data || {};
  if (!Array.isArray(markets)) return {};
  const result: {
    [key: string]: string[];
  } = {};
  for (const market of markets) {
    const [parentCode, childCode] = market?.split('_split_') || [];
    if (!Array.isArray(result[parentCode])) {
      result[parentCode] = [];
    }
    if (!result[parentCode].includes(childCode)) {
      result[parentCode].push(childCode);
    }
  }
  return result;
};

const getStudioJumpable = (studioJump?: IStudioJumpSystem): boolean => {
  if (!studioJump) return false;
  return STUDIO_JUMP_SYSTEMS.includes(studioJump);
};

const getStudioJumpUrlCrossSystem = (params?: {
  studioJump?: IStudioJumpSystem;
  studioId?: string;
  studioName?: string;
}): string => {
  const { studioJump = '', studioId = '', studioName = '' } = params || {};
  if (!getStudioJumpable(studioJump) || !studioId) return '';
  let url = '';
  // 支持跳转到GPOP的studio详情页
  if (studioJump === StudioJumpSystem.GPop) {
    const filter = encodeURIComponent(
      JSON.stringify({
        studioId,
        studioName,
        // 用于标识左侧菜单栏当前选中高亮
        menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'],
      }),
    );
    url = `${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${filter}`;
  }
  // 支持跳转到情报的公司详情页
  if (studioJump === StudioJumpSystem.Intelligence) {
    const basePath = getBasePath();
    const path = getCompanyDetailsPath({
      companyId: studioId,
      tab: 'Summary',
    });
    url = path?.startsWith('/') ? basePath + path : `${basePath}/${path}`;
  }
  return url;
};

const handleStudioJumpCrossSystem = (
  studioJump?: IStudioJumpSystem,
  url?: string,
  e?: React.MouseEvent<HTMLElement, MouseEvent>,
) => {
  if (!queryJumpable(e) || !getStudioJumpable(studioJump) || !url) return;
  // 支持跳转到GPOP的studio详情页
  if (studioJump === StudioJumpSystem.GPop) {
    history.push(url);
  }
  // 支持跳转到情报的公司详情页
  if (studioJump === StudioJumpSystem.Intelligence) {
    window.open(url, '_blank');
  }
};

const getMatchedTabId = (tabs?: any[], filterObj?: Record<string, any>): string => {
  if (!Array.isArray(tabs) || !tabs.length) return '';
  const { tab: tabIdOfFilterObj } = filterObj || {};
  const tab = tabs.find((tab) => {
    const { id: tabId } = tab || {};
    if (tabId === tabIdOfFilterObj) return true;
    return tabIdOfFilterObj === 'management' && tabId === 'management_boardGovernance';
  });
  return tab?.id || tabs[0]?.id || '';
};

const getStudioModuleName = (moduleName: string, studioName?: string): string => {
  const studioNameTrim = studioName?.trim() || '';
  return !studioNameTrim ? moduleName : `${studioNameTrim}-${moduleName}`;
};

export {
  getNumValueText,
  getValueWithUnitText,
  getLastYearAndCurrentYear,
  getOptionsByStringValues,
  getStudioDataTypeDefaultValue,
  formatStudioOptions,
  formatStudioValues,
  getStudioJumpable,
  getStudioJumpUrlCrossSystem,
  handleStudioJumpCrossSystem,
  getMatchedTabId,
  getStudioModuleName,
};
